/**
 * Jurídico: contencioso (ADR 0064). Processos judiciais, administrativos, arbitragens e mediações,
 * com natureza, instância, partes, responsáveis, escritório (pela demanda externa), movimentações
 * e documentos. Nenhum prazo processual é calculado.
 */
import { z } from 'zod';
import { LEGAL_COMPARTMENTS, LEGAL_CONFIDENTIALITY } from './legal.ts';

export const LAWSUIT_KINDS = ['judicial', 'administrative', 'arbitration', 'mediation'] as const;
export const LAWSUIT_NATURES = [
  'labor',
  'civil',
  'consumer',
  'tax',
  'corporate',
  'intellectual_property',
  'regulatory',
  'other',
] as const;
export const LAWSUIT_INSTANCES = [
  'first',
  'second',
  'superior',
  'supreme',
  'administrative',
  'arbitral',
  'other',
] as const;
export const LAWSUIT_ROLES = ['defendant', 'plaintiff', 'interested', 'other'] as const;
export const LAWSUIT_STATUSES = ['active', 'suspended', 'settled', 'closed'] as const;
/** Encerrados: por acordo ou por decisão final/arquivamento (exigem a data de encerramento). */
export const LAWSUIT_CLOSED: readonly string[] = ['settled', 'closed'];

const text = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const id = z.uuid().nullable().optional();
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

export const legalLawsuitInput = z
  .object({
    legalEntityId: z.uuid(),
    kind: z.enum(LAWSUIT_KINDS),
    nature: z.enum(LAWSUIT_NATURES),
    caseNumber: text(100),
    court: text(300),
    venue: text(300),
    instance: z.enum(LAWSUIT_INSTANCES).nullable().optional(),
    lootRole: z.enum(LAWSUIT_ROLES),
    subject: z.string().trim().min(1).max(300),
    description: text(10000),
    counterpartyName: text(300),
    partnerId: id,
    employeeId: id,
    internalOwnerId: id,
    matterId: id,
    externalOwner: text(200),
    claimAmount: z
      .string()
      .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números com ponto (ex.: 1500.00)')
      .nullable()
      .optional()
      .or(z.literal('').transform(() => null)),
    claimCurrency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .nullable()
      .optional()
      .or(z.literal('').transform(() => null)),
    filedOn: day,
    status: z.enum(LAWSUIT_STATUSES).default('active'),
    closedOn: day,
    outcomeNote: text(4000),
    requestId: id,
    contractId: id,
    confidentiality: z.enum(LEGAL_CONFIDENTIALITY).default('restricted'),
    compartment: z.enum(LEGAL_COMPARTMENTS).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (!!v.claimAmount !== !!v.claimCurrency) {
      issue(v.claimAmount ? 'claimCurrency' : 'claimAmount', 'Informe o valor e a moeda juntos');
    }
    if (LAWSUIT_CLOSED.includes(v.status) && !v.closedOn) {
      issue('closedOn', 'Informe a data de encerramento');
    }
    if (!LAWSUIT_CLOSED.includes(v.status) && v.closedOn) {
      issue('closedOn', 'Só processos encerrados têm data de encerramento');
    }
    if (v.employeeId && v.nature !== 'labor') {
      issue('employeeId', 'Colaborador só em processo trabalhista');
    }
  });
export type LegalLawsuitInput = z.infer<typeof legalLawsuitInput>;

/**
 * Sigilo reforçado da natureza trabalhista (D-21): sempre no assunto sensível "trabalhista" e,
 * no mínimo, restrito. As demais naturezas usam o sigilo escolhido.
 */
export function lawsuitSecrecy(input: {
  nature: string;
  confidentiality: string;
  compartment?: string | null;
}): { confidentiality: string; compartment: string | null } {
  if (input.nature !== 'labor') {
    return { confidentiality: input.confidentiality, compartment: input.compartment ?? null };
  }
  return {
    confidentiality: input.confidentiality === 'internal' ? 'restricted' : input.confidentiality,
    compartment: 'labor',
  };
}

/** Movimentação do processo: o que aconteceu e quando (informado pelo responsável). */
export const legalLawsuitMovementInput = z.object({
  occurredOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  note: z.string().trim().min(3).max(4000),
});

/**
 * Avaliação do processo (D-20): probabilidade de perda, valor da causa e exposição estimada, ou o
 * valor de acordo ou decisão. Informação jurídica, nunca provisão contábil.
 */
export const LAWSUIT_PROBABILITIES = ['probable', 'possible', 'remote'] as const;
export const LAWSUIT_VALUATION_KINDS = ['risk', 'settlement', 'decision'] as const;

const money = z.string().regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números com ponto (ex.: 1500.00)');
const optionalMoney = money
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

export const legalLawsuitValuationInput = z
  .object({
    kind: z.enum(LAWSUIT_VALUATION_KINDS),
    probability: z.enum(LAWSUIT_PROBABILITIES).nullable().optional(),
    claimAmount: optionalMoney,
    exposureAmount: optionalMoney,
    amount: optionalMoney,
    currency: z.string().regex(/^[A-Z]{3}$/, 'Escolha a moeda'),
    recordedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe a data'),
    rationale: z.string().trim().min(5, 'Explique a fundamentação').max(4000),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message });
    if (v.kind === 'risk') {
      if (!v.probability) issue('probability', 'Escolha a probabilidade de perda');
      if (!v.exposureAmount) issue('exposureAmount', 'Informe a exposição estimada');
    } else if (!v.amount) {
      issue('amount', v.kind === 'settlement' ? 'Informe o valor do acordo' : 'Informe o valor');
    }
  });
export type LegalLawsuitValuationInput = z.infer<typeof legalLawsuitValuationInput>;
